import { ActionResponse, ActionStatusHelper, DOMAIN_CONFIGS, FeedBackType, formatName, LONG_PROCESS_TOAST_DURATION, MessageHelper, ResponseHook, UIFeedbackHooks } from "@/Shared";
import { useEffect, useState } from "react";
import { AccessRightModel, AuthService } from "@/Domains/Auth";
import { UserModel } from "../UserModel";

/**
 * usePasswordForm hook – Manages user list configuration, state, and interactions.
 *
 * This hook encapsulates all the logic required to:
 * - Fetch and enrich user entities
 * - Provide configuration for a DataTable or DataCardList component
 * - Handle row actions: view, create, edit, delete
 * - Manage toast notifications for user feedback
 * - Report fetch/delete status for conditional UI rendering
 *
 * Exposes:
 * - userConfig: DataTableConfig object used by UI components
 * - isFetchRunning, isFetchFailed, fetchErrorMessage: status of fetch operation
 * - isDeleteSucceeded, isDeleteFailed, deleteErrorMessage: status of deletion
 * - setDeleteIdle: resets delete status to 'idle' (used to clear state)
 * 
 * Internally, it:
 * - Enriches users using `useEnrichedUser`
 * - Builds table headers with user data and enums
 * 
 * Author: Samano CASTRE  
 * Date: August 3, 2025
 */
export function useGeneratePassword(
	uiFeedbackHooks: UIFeedbackHooks = {} as UIFeedbackHooks,
	accessRight: AccessRightModel
) {

	const { toastHook, confirmHook } = uiFeedbackHooks;

	const [response, setResponse] = useState<ActionResponse>({ status: 'idle' });

	const {domainType, domainLabel} = DOMAIN_CONFIGS.User;

	// Toast management based on form status
	useEffect(() => {
		const { isLoading, isFailed, isSucceeded } = ActionStatusHelper.is(response.status);
		if (isSucceeded) {
			openToast(MessageHelper.PASSWORD_GENERATING.SUCCESS(domainLabel), FeedBackType.SUCCESS);
		}
		if (isFailed) {
			openToast(MessageHelper.PASSWORD_GENERATING.ERROR(domainLabel) || "", FeedBackType.ERROR);
		}

		if (isLoading) {
			openToast("Soumission du formulaire en cours...", FeedBackType.INFO, LONG_PROCESS_TOAST_DURATION);
		}

	}, [response.status, response.error]);

	const openToast = (message: string, type: FeedBackType, duration? : number, onClose?: () => void) => {
		toastHook.openToast({ data: { message, type, duration }, onClose });
	};


	const handleSubmit = async (userId: number) => {
		ResponseHook.loading(setResponse);
		AuthService.resetPassword(userId).then(async data => {
			ResponseHook.success(setResponse);
		}).catch(error =>
			ResponseHook.error(setResponse, error, "Erreur lors de la réinitialisation du mot de passe de l'agent.")
		);
	};

	const handleGeneratePassword = async (agent: UserModel) => {
		if (!accessRight.canExecute(domainType)) {
			toastHook.openToast?.({
				data: {
					message: MessageHelper.PASSWORD_GENERATING.NOT_ALLOWED(domainLabel),
					type: FeedBackType.WARNING,
				}
			});
			return;
		}

		const confirmed = await confirmHook.openConfirm?.({
			title: MessageHelper.MODAL.CONFIRM.TITLE,
			content: MessageHelper.MODAL.CONFIRM.PASSWORD.GENERATING(domainLabel, `${formatName(agent.person)}`),
		});

		if (!confirmed) {
			return;
		}

		toastHook.openToast?.({
			data: {
				message: MessageHelper.PASSWORD_GENERATING.LOADING(domainLabel),
				type: FeedBackType.INFO,
				duration : LONG_PROCESS_TOAST_DURATION
			}
		});

		await handleSubmit(agent.id);
	}

	return {
		response,
		handleGeneratePassword
	}
}